import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import { useDispatchApp, useSelectorApp } from "../../../../lib/redux";
import { startGetAdPreview } from "../../../../actions/adsCampaigns";
import { Controller, useForm, useFormContext } from "react-hook-form";
import { formattedIframeByType } from "../../../../services/adsCampaigns";
import {
  changeCreativeWithOptionalTextRegeneration,
  getMatchedCreativeEntryForAdSlot,
} from "../../../../services/creativeChangeWithTextRegeneration";
import { useTranslationApp } from "../../../../lib/i18next";
import {
  ADS_CAMPAIGN_AD_FORMAT_TIKTOK_FEED_CAROUSEL_ADS,
  ADS_CAMPAIGN_AD_FORMAT_TIKTOK_FEED_SINGLE_VIDEO,
  ADS_CAMPAIGN_MEDIA_FROM_TIKTOK_POST,
  ADS_CAMPAIGN_OBJECTIVE_ENGAGEMENT_OPTION,
  DELAY_DEBOUNCE,
  VIDEO_FORMATS,
  MIN_DIMENSIONS_CONTENT,
  TIKTOK,
  ADS_TIKTOK_CAMPAIGN_OBJECTIVES_VALID_WITH_IMAGES,
  ADS_CAMPAIGN_CAROUSEL_FORMAT,
  ADS_CAMPAIGN_OBJECTIVE_LEADS_OPTION,
  ADS_CAMPAIGN_TIKTOK_DESTINATION_TYPE_INSTANT_PAGE,
} from "../../../../utils/constants";
import {
  BUTTON_CHANGE_CONTENT,
  BUTTON_RETRY,
  BUTTON_SELECT_CONTENT,
  FIELD_AD_FORMAT_REQUIRED_AD_PREVIEW,
  FIELD_CALL_TO_ACTION_REQUIRED_AD_PREVIEW,
  FIELD_CONTENT_IS_BEING_PROCESSED,
  FIELD_IDENTITY_TIK_TOK_ID_REQUIRED_AD_PREVIEW,
  FIELD_MEDIA_URL_REQUIRED_AD_PREVIEW,
  FIELD_MUSIC_URL_REQUIRED_AD_PREVIEW,
  META_AD_PREVIEW_AD_BLOCKER_NOTICE,
  FIELD_REQUIRED,
  FIELD_TITLES_REQUIRED_AD_PREVIEW_TIKTOK,
  FORMAT_PLACEHOLDER,
} from "../../../../i18n/keysTranslations";
import Select from "../../../../components/Form/Select";
import IconButtonAddContent from "../../../../components/Buttons/IconButtonAddContent";
import { getPathDatabaseByRole } from "../../../../actions/auth";
import { getReactHookFormNestedError } from "../../../../utils/errors";
import { useMediaQuery } from "@mui/material";
import _ from "lodash";
import TikTokBusinessContainerIntegration from "../../../../components/TikTokBusiness/TikTokBusinessContainerIntegration";
import CardMedia from "../../../../components/Media/CardMedia";
import { getExtensionFile } from "../../../../utils/string";
import Button from "../../../../components/Buttons/Button";
import RefreshIcon from "@mui/icons-material/Refresh";
import CircularProgress from "@mui/material/CircularProgress";
import TooltipOptimization from "../../../Tooltip/TooltipOptimization";
import AdPreviewRegeneratingTextsOverlay from "../../AdPreviewRegeneratingTextsOverlay";
import { buildCreativeOptimizationPrompt } from "../../../../utils/creativeOptimizationContext";
const FORMATS_TIKTOK_VIDEO = [ADS_CAMPAIGN_AD_FORMAT_TIKTOK_FEED_SINGLE_VIDEO];
const FORMATS_TIKTOK_IMAGE = [ADS_CAMPAIGN_AD_FORMAT_TIKTOK_FEED_CAROUSEL_ADS];

const getAvailableFormats = ({ mediaData, isVideo }) => {
  if (mediaData?.from === ADS_CAMPAIGN_MEDIA_FROM_TIKTOK_POST) {
    return FORMATS_TIKTOK_VIDEO;
  }
  return isVideo ? FORMATS_TIKTOK_VIDEO : FORMATS_TIKTOK_IMAGE;
};

const serializeCopyFieldsSignature = (titles = []) =>
  (titles || []).map((item) => item?.value ?? "").join("\u0001");

const getConfigurationPreviewType = ({ mediaUrls = [], platformData = {} }) => {
  const showAdPreview = Boolean(
    platformData?.adAccountID && platformData?.integrationID
  );
  const showMediaUpload = Boolean(!showAdPreview && mediaUrls?.length > 0);
  return {
    showAdPreview,
    showMediaUpload,
  };
};

const AdPreviewTiktok = forwardRef((props, ref) => {
  const {
    adID,
    platform,
    mediaUrls = [],
    mediaData = [],
    titles,
    basicPath,
    basicPathAdGroup,
  } = props;
  const [currentMediaIndex] = useState(0);
  const [formats, setFormats] = useState([]);
  const [adPreviewSrc, setAdPreviewSrc] = useState("");
  const [triggerGeneratePreview, setTriggerGeneratePreview] = useState(0);
  const [isRegeneratingTexts, setIsRegeneratingTexts] = useState(false);
  const [isLoadingIframe, setIsLoadingIframe] = useState(true);
  const [hasPreviousImage, setHasPreviousImage] = useState(false);
  const [error, setError] = useState(null);
  const abortControllerRef = useRef(null);

  const showIframePreviewLoader = isLoadingIframe && !isRegeneratingTexts;

  const currentMediaUrl = mediaUrls[currentMediaIndex];
  const currentMediaData = mediaData[currentMediaIndex];

  const {
    isGeneralDisabledFields,
    campaignID,
    watch,
    setValue,
    getValues,
    previewsCached,
    onChangePreviewsCached,
  } = useFormContext();

  const platformData = watch(`platformsData.${platform}`) || {};
  const isIOS14Campaign = platformData?.isIOS14Campaign;
  const isIncompatibleCampaign = platformData?.isIncompatible;
  const isIncompatibleAdGroup =
    getValues(`${basicPathAdGroup}.isIncompatible`) || false;
  const isCarousel = watch(`${basicPath}.isCarousel`);
  const musicUrl = watch(`${basicPath}.musicUrl`);
  const isIncompatibleAd = getValues(`${basicPath}.isIncompatible`) || false;
  const callToAction = watch(`${basicPath}.callToAction`) || "";
  const callToActions = watch(`${basicPath}.callToActions`) || [];
  const id = watch(`${basicPath}.id`);
  const formTitlesWatch = watch(`${basicPath}.titles`);
  const copyFieldsSignature = serializeCopyFieldsSignature(formTitlesWatch);

  const isDisabledFields =
    isGeneralDisabledFields ||
    isIncompatibleCampaign ||
    isIncompatibleAdGroup ||
    isIncompatibleAd;

  const isPreviewFormatSelectDisabled =
    isGeneralDisabledFields &&
    !isIncompatibleCampaign &&
    !isIncompatibleAdGroup &&
    !isIncompatibleAd;

  const extension = getExtensionFile(currentMediaUrl);
  const isVideo = VIDEO_FORMATS.includes(extension);

  const {
    control,
    errors,
    setValue: setValueComponent,
    watch: watchComponent,
  } = useForm({
    defaultValues: {
      adFormat: formats[0],
    },
  });
  const errorMediaUrl = getReactHookFormNestedError(
    errors,
    `${basicPath}.mediaUrls`
  );
  const adFormat = watchComponent("adFormat");

  const destinationTypeAdGroup = watch(`${basicPathAdGroup}.destinationType`);
  const instantForm = watch(`${basicPath}.instantForm`);
  const mediaIDs = watch(`${basicPath}.mediaIDs`);
  const destinationUrls = watch(`${basicPath}.destinationUrls`) || [];
  const platforms = watch("platforms");
  const websiteURL = watch("websiteURL");
  const language = watch("language");
  const objective = platformData?.objective;
  const isInstantFormLead =
    objective === ADS_CAMPAIGN_OBJECTIVE_LEADS_OPTION &&
    destinationTypeAdGroup ===
      ADS_CAMPAIGN_TIKTOK_DESTINATION_TYPE_INSTANT_PAGE;

  const isEngagementAndOnVideo =
    objective === ADS_CAMPAIGN_OBJECTIVE_ENGAGEMENT_OPTION &&
    destinationTypeAdGroup === "on_video";

  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();
  const isMobile = useMediaQuery(() => "(max-width: 360px)");

  const integrations = useSelectorApp((state) => state?.user?.integrations);
  const tikTokBusinessIntegrations = integrations?.tiktokBusiness || {};
  const noHasPlatformData =
    Object.values(tikTokBusinessIntegrations).length === 0;

  const { showAdPreview, showMediaUpload } = getConfigurationPreviewType({
    mediaData,
    mediaUrls,
    platformData,
  });

  const { userID } = dispatch(getPathDatabaseByRole());
  const identityTikTokID = platformData?.identityTikTokID;

  const validateDataToGeneratePreview = () => {
    if (!adFormat) {
      return { ok: false, message: FIELD_AD_FORMAT_REQUIRED_AD_PREVIEW };
    }
    if (!callToAction && callToActions?.length === 0) {
      return { ok: false, message: FIELD_CALL_TO_ACTION_REQUIRED_AD_PREVIEW };
    }
    if (!identityTikTokID) {
      return {
        ok: false,
        message: FIELD_IDENTITY_TIK_TOK_ID_REQUIRED_AD_PREVIEW,
      };
    }
    if (_.isEmpty(mediaUrls)) {
      return {
        ok: false,
        message: FIELD_MEDIA_URL_REQUIRED_AD_PREVIEW,
      };
    }
    if (isCarousel && !musicUrl) {
      return { ok: false, message: FIELD_MUSIC_URL_REQUIRED_AD_PREVIEW };
    }
    const title = titles?.[0]?.value || "";
    if (!title || title?.trim()?.length === 0) {
      return { ok: false, message: FIELD_TITLES_REQUIRED_AD_PREVIEW_TIKTOK };
    }
    return { ok: true };
  };

  const onChangeAdPreview = (value) => {
    setAdPreviewSrc(value);
  };

  const onGetInformationIframe = () => {
    const { height, width, src } = formattedIframeByType({
      type: adFormat,
      iframe: adPreviewSrc || "",
      isMobile,
      isCarousel,
      platform: TIKTOK,
    });

    return {
      height,
      width,
      src,
    };
  };

  const { height, width, src } = onGetInformationIframe();

  const getInitialData = async (type) => {
    const someMediaUrlIsBlob = mediaUrls?.some((mediaUrl) =>
      mediaUrl?.includes("blob:")
    );
    if (someMediaUrlIsBlob) {
      return;
    }
    if (!showAdPreview) {
      setIsLoadingIframe(true);
      return;
    }
    const isValidData = validateDataToGeneratePreview();
    if (!isValidData.ok) {
      setIsLoadingIframe(true);
      return setError(t(isValidData.message));
    }
    const formTitles = (getValues(`${basicPath}.titles`) || []).map(
      (title) => title?.value ?? ""
    );
    const callToActionID = getValues(`${basicPath}.callToActionID`);
    const response = await dispatch(
      startGetAdPreview(
        {
          platform,
          campaignID,
          adID,
          mediaUrls,
          mediaData,
          mediaIDs,
          callToAction,
          callToActionID,
          titles: formTitles,
          destinationUrls: destinationUrls
            ? destinationUrls.map((url) => url.value)
            : [],
          instantFormID: isInstantFormLead ? instantForm?.id : undefined,
          identityTikTokID,
          adFormats: [adFormat],
          platformData,
          destinationType: destinationTypeAdGroup,
          isCarousel,
          isIOS14Campaign,
          musicUrl,
        },
        abortControllerRef.current.signal
      )
    );
    if (!response.ok) {
      if (response.aborted) {
        return;
      }
      setError(response.message);
    }
    if (response.ok) {
      const { adPreviews: adPreviewsResponse } = response.data;
      const newPreview = adPreviewsResponse[adFormat];
      let newObjectPreviewsCached = {};
      if (type === "values") {
        newObjectPreviewsCached[adFormat] = newPreview;
      } else {
        newObjectPreviewsCached = {
          ...previewsCached[id],
          [adFormat]: newPreview,
        };
      }
      onChangePreviewsCached({
        ...previewsCached,
        [id]: newObjectPreviewsCached,
      });
      onChangeAdPreview(newPreview);
    }
    setIsLoadingIframe(true);
  };

  useEffect(() => {
    if (formats.includes(adFormat)) return;
    setValueComponent("adFormat", formats[0]);
    // eslint-disable-next-line
  }, [isCarousel]);

  useEffect(() => {
    const formatsByConfiguration = getAvailableFormats({
      isVideo,
      mediaData: currentMediaData,
    });
    setValueComponent("adFormat", formatsByConfiguration[0]);
    setFormats(formatsByConfiguration);
    // eslint-disable-next-line
  }, [isVideo, isCarousel, currentMediaData]);

  useEffect(() => {
    if (!adFormat) return;
    if (abortControllerRef?.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    setError(null);

    setIsLoadingIframe(true);

    // Use debounced function for values changes
    const timeoutId = setTimeout(() => {
      getInitialData("values");
    }, DELAY_DEBOUNCE * 2.5);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line
  }, [
    currentMediaUrl,
    currentMediaData?.postID,
    callToAction,
    identityTikTokID,
    triggerGeneratePreview,
    destinationTypeAdGroup,
    instantForm?.id,
    isCarousel,
    musicUrl,
    copyFieldsSignature,
  ]);

  useEffect(() => {
    if (!adFormat) return;
    if (abortControllerRef?.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    setError(null);

    if (previewsCached?.[id]?.[adFormat]) {
      onChangeAdPreview(previewsCached[id][adFormat]);
      setIsLoadingIframe(true);
      setHasPreviousImage(true);
      return;
    }

    setAdPreviewSrc("");
    setIsLoadingIframe(true);

    // Use debounced function for adFormat changes
    const timeoutId = setTimeout(() => {
      getInitialData("adFormat");
    }, DELAY_DEBOUNCE * 2.5);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line
  }, [adFormat]);

  const previewRefreshTimeoutRef = useRef(null);

  const schedulePreviewRefresh = (type, delayMs = DELAY_DEBOUNCE * 2.5) => {
    if (!showAdPreview || !adFormat) {
      return;
    }

    if (previewRefreshTimeoutRef.current) {
      clearTimeout(previewRefreshTimeoutRef.current);
    }

    previewRefreshTimeoutRef.current = setTimeout(() => {
      previewRefreshTimeoutRef.current = null;
      getInitialData(type);
    }, delayMs);
  };

  const onTriggerGeneratePreview = (from = "") => {
    setIsLoadingIframe(true);
    if (from === "content" || from === "texts") {
      onChangePreviewsCached({
        ...previewsCached,
        [id]: {},
      });
    }
    setTriggerGeneratePreview((prev) => prev + 1);

    if (from === "texts") {
      schedulePreviewRefresh("texts", 0);
    } else if (from === "content") {
      schedulePreviewRefresh("values", DELAY_DEBOUNCE * 2.5);
    }
  };

  useImperativeHandle(ref, () => ({
    generatePreview: onTriggerGeneratePreview,
  }));

  const onChangeAdFormat = (direction) => {
    const currentIndex = formats.indexOf(adFormat);
    let nextIndex;
    if (direction === "next") {
      nextIndex = currentIndex + 1;
      if (nextIndex >= formats.length) {
        nextIndex = 0;
      }
    }
    if (direction === "back") {
      nextIndex = currentIndex - 1;
      if (nextIndex < 0) {
        nextIndex = formats.length - 1;
      }
    }
    setValueComponent("adFormat", formats[nextIndex]);
  };

  const commonSxContainer = {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    gap: 1.5,
    maxWidth: width,
    width: "100%",
  };

  if (showMediaUpload) {
    return (
      <Box sx={commonSxContainer}>
        {noHasPlatformData && !isDisabledFields && (
          <TikTokBusinessContainerIntegration
            onChangeIntegration={({ integrationID, adAccountID }) => {
              setValue(
                `platformsData.${platform}.integrationID`,
                integrationID,
                { shouldValidate: true, shouldDirty: true }
              );
              setValue(`platformsData.${platform}.adAccountID`, adAccountID, {
                shouldValidate: true,
                shouldDirty: true,
              });
            }}
          />
        )}
        <SelectNewContent
          isEngagementAndOnVideo={isEngagementAndOnVideo}
          platform={platform}
          userID={userID}
          adID={adID}
          basicPath={basicPath}
          basicPathAdGroup={basicPathAdGroup}
          control={control}
          mediaUrl={currentMediaUrl}
          currentMediaIndex={currentMediaIndex}
          onTriggerGeneratePreview={onTriggerGeneratePreview}
          onRegeneratingTextsChange={setIsRegeneratingTexts}
          platforms={platforms}
          platformData={platformData}
          websiteURL={websiteURL}
          language={language}
          errorMediaUrl={errorMediaUrl}
          isDisabledFields={isDisabledFields}
          showButton={true}
        />
        <CardMedia
          url={currentMediaUrl}
          sx={{
            height,
            maxWidth: width,
            width: { xs: "100%", sm: width },
          }}
        />
        <AdPreviewRegeneratingTextsOverlay open={isRegeneratingTexts} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        maxWidth: width,
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
        }}
      >
        <SelectAdPreviewFormat
          control={control}
          errors={errors}
          onChangeAdFormat={onChangeAdFormat}
          formats={formats}
          disabled={isPreviewFormatSelectDisabled}
        />
        <SelectNewContent
          isEngagementAndOnVideo={isEngagementAndOnVideo}
          platform={platform}
          userID={userID}
          adID={adID}
          basicPath={basicPath}
          basicPathAdGroup={basicPathAdGroup}
          control={control}
          mediaUrl={currentMediaUrl}
          currentMediaIndex={currentMediaIndex}
          onTriggerGeneratePreview={onTriggerGeneratePreview}
          onRegeneratingTextsChange={setIsRegeneratingTexts}
          platforms={platforms}
          platformData={platformData}
          websiteURL={websiteURL}
          language={language}
          errorMediaUrl={errorMediaUrl}
          isDisabledFields={isDisabledFields}
          showButton={true}
        />
      </Box>
      {showAdPreview && (
        <>
          <AdMediaPreview
            isMobile={isMobile}
            isLoadingIframe={showIframePreviewLoader}
            src={src}
            width={width}
            height={height}
            error={error}
            hasPreviousImage={hasPreviousImage}
            onChangeIsLoadingIframe={setIsLoadingIframe}
            onChangeHasPreviousImage={setHasPreviousImage}
            onTriggerGeneratePreview={onTriggerGeneratePreview}
          />
          {!error && !showIframePreviewLoader && (
            <Box
              sx={{
                alignSelf: "stretch",
                p: 1,
                borderRadius: 1,
                border: "1px solid",
                borderColor: "warning.main",
              }}
            >
              <Typography
                component="p"
                variant="caption"
                sx={{
                  m: 0,
                  color: "warning.dark",
                  fontSize: "0.72rem",
                  lineHeight: 1.4,
                }}
              >
                {t(META_AD_PREVIEW_AD_BLOCKER_NOTICE)}
              </Typography>
            </Box>
          )}
        </>
      )}
      <AdPreviewRegeneratingTextsOverlay open={isRegeneratingTexts} />
    </Box>
  );
});

const AdMediaPreview = ({
  isMobile,
  isLoadingIframe,
  src,
  width,
  height,
  error,
  hasPreviousImage,
  onChangeIsLoadingIframe,
  onChangeHasPreviousImage,
  onTriggerGeneratePreview,
}) => {
  const { t } = useTranslationApp();
  return (
    <Box>
      {error && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Button
            type="button"
            onClick={() => onTriggerGeneratePreview()}
            startIcon={<RefreshIcon sx={{ fontSize: "1.5rem !important" }} />}
            color="secondary"
            sx={{
              width: "fit-content",
            }}
          >
            {t(BUTTON_RETRY)}
          </Button>
          <Typography
            variant="body2"
            color="error"
            align="center"
            sx={{
              "& *": {
                p: "revert",
                m: "revert",
                listStyle: "revert",
                textDecoration: "revert",
              },
            }}
            dangerouslySetInnerHTML={{
              __html: error,
            }}
          />
        </Box>
      )}
      {!error && (
        <Box
          sx={{
            maxWidth: width,
            width: "100%",
            height,
            position: "relative",
          }}
        >
          {isLoadingIframe && !src && (
            <Skeleton
              variant="rectangular"
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                maxWidth: width,
                width: "100%",
                height,
                zIndex: 3,
              }}
            />
          )}

          {/* Loader overlay para nueva imagen */}
          {isLoadingIframe && hasPreviousImage && src && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(255, 255, 255, 0.5)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 4,
              }}
            >
              <CircularProgress size={40} />
            </Box>
          )}
          {src && (
            <iframe
              src={src}
              onLoad={() => {
                onChangeIsLoadingIframe(false);
                onChangeHasPreviousImage(true);
              }}
              scrolling="no"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                border: isMobile ? "none" : "1px solid white",
                overflow: "hidden",
                maxWidth: width,
                width: "100%",
                height,
                zIndex: 2,
              }}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

const SelectNewContent = ({
  basicPath,
  basicPathAdGroup,
  control,
  userID,
  adID,
  currentMediaIndex,
  onTriggerGeneratePreview,
  onRegeneratingTextsChange = () => {},
  videoData: videoDataProp,
  mediaUrl,
  platformData,
  websiteURL,
  language,
  errorMediaUrl,
  isDisabledFields,
  showButton = true,
}) => {
  const { t } = useTranslationApp();
  const {
    setValue,
    watch,
    getValues,
    trigger,
    creativeOptimizations,
    onRemoveCreativeOptimization,
  } = useFormContext();
  const dispatch = useDispatchApp();

  const creativeEntry = getMatchedCreativeEntryForAdSlot({
    creativeOptimizations,
    basicPath,
  });

  const socialNetworkURL = watch("socialNetworkURL");
  const destinationType = watch(`${basicPathAdGroup}.destinationType`);
  const productContext = watch("productContext");
  const targetContext = watch("targetContext");
  const ageRange = watch(`${basicPathAdGroup}.ageRange`);
  const genders = watch(`${basicPathAdGroup}.genders`);
  const isCarousel = watch(`${basicPath}.isCarousel`);
  const platformsData = watch("platformsData") || {};
  const objective = platformsData?.[TIKTOK]?.objective;
  const isCarouselAdsAvailable =
    ADS_TIKTOK_CAMPAIGN_OBJECTIVES_VALID_WITH_IMAGES.includes(objective);

  const onChangeContent = async (content) => {
    await changeCreativeWithOptionalTextRegeneration({
      platform: TIKTOK,
      content,
      basicPath,
      adID,
      currentMediaIndex,
      isCarousel,
      dispatch,
      getValues,
      setValue,
      watch,
      trigger,
      t,
      language,
      campaignContext: watch("context") || "",
      creativeOptimizations,
      onRemoveCreativeOptimization,
      onTriggerGeneratePreview,
      onRegeneratingTextsChange,
    });
  };

  useEffect(() => {
    if (!creativeEntry?.title) return;
    const element = document.getElementById(
      `${basicPath}.mediaUrls.[${currentMediaIndex}]`
    );
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    // eslint-disable-next-line
  }, [creativeEntry?.slotKey, basicPath]);

  if (isCarousel || !showButton) return;
  const FORMATS_MEDIA_OBJECTIVE = VIDEO_FORMATS;

  const showTooltip = !_.isEmpty(creativeEntry);
  return (
    <TooltipOptimization
      isOpen={showTooltip}
      titleTooltip={creativeEntry?.title}
      actionTooltip={creativeEntry?.action}
    >
      <Box
        id={`${basicPath}.mediaUrls.[${currentMediaIndex}]`}
        data-field={`${basicPath}.mediaUrls.[${currentMediaIndex}]`}
        sx={{
          width: "100%",
        }}
      >
        <Controller
          name={`${basicPath}.mediaUrls.[${currentMediaIndex}]`}
          control={control}
          rules={{
            required: {
              value: true,
              message: t(FIELD_REQUIRED),
            },
            validate: (value) => {
              if (value?.includes("blob:")) {
                return t(FIELD_CONTENT_IS_BEING_PROCESSED);
              }
            },
          }}
          render={() => (
            <IconButtonAddContent
              id={`add-content-media-${basicPath}`}
              mode="button"
              disabled={isDisabledFields}
              minDimensions={MIN_DIMENSIONS_CONTENT}
              showButtonUpload={true}
              pathStorage={`adsCampaigns/${userID}/`}
              modeUpload="sync"
              onAddContent={(contents) => {
                onChangeContent(contents[0]);
              }}
              initialValues={{
                language,
                platform: TIKTOK,
                ...(creativeEntry
                  ? { prompt: buildCreativeOptimizationPrompt(creativeEntry) }
                  : {}),
              }}
              onCallbackSaveCanva={(mediaUrl) => {
                const canvaContent = {
                  url: mediaUrl,
                  type: "upload",
                };
                onChangeContent(canvaContent);
              }}
              multiple={false}
              acceptedFormats={FORMATS_MEDIA_OBJECTIVE.concat(
                isCarouselAdsAvailable ? [ADS_CAMPAIGN_CAROUSEL_FORMAT] : []
              )}
              urlContent={mediaUrl}
              textButton={
                mediaUrl ? BUTTON_CHANGE_CONTENT : BUTTON_SELECT_CONTENT
              }
              showGenerateWithAIOption={true}
              showUploadDeviceOption={true}
              showGalleryOption={true}
              destinationType={destinationType}
              propsPostsAdsPlatforms={platformsData}
              propsGenerateAI={{
                videoData: videoDataProp?.videoID
                  ? { ...videoDataProp, id: videoDataProp?.videoID }
                  : null,
                websiteURL,
                socialNetworkURL,
                language,
                integrationID: platformData?.integrationID,
                adAccountID: platformData?.adAccountID,
                campaignContext: {
                  objective,
                  productContext,
                  targetContext,
                  genders,
                  ageRange,
                },
              }}
              color="secondary"
              sx={{
                gap: 1,
                alignItems: "flex-start",
                width: "100%",
                mb: 1,
              }}
              sxButton={{
                width: "100%",
              }}
            />
          )}
        />
        {errorMediaUrl && (
          <Typography
            color="error"
            variant="caption"
            dangerouslySetInnerHTML={{
              __html: errorMediaUrl?.message,
            }}
            sx={{
              "& *": {
                p: "revert",
                m: "revert",
                listStyle: "revert",
                textDecoration: "revert",
              },
            }}
          />
        )}
      </Box>
    </TooltipOptimization>
  );
};

const SelectAdPreviewFormat = ({
  control,
  errors,
  formats,
  disabled = false,
}) => {
  const { t } = useTranslationApp();
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        width: "100%",
      }}
    >
      <Select
        variant="filled"
        name="adFormat"
        label={t(FORMAT_PLACEHOLDER)}
        options={formats.map((format) => ({
          value: format,
          label: t(format),
        }))}
        control={control}
        errors={errors}
        disabled={disabled}
        fullWidth={true}
        sx={{
          mb: 1,
        }}
      />
    </Box>
  );
};

AdPreviewTiktok.displayName = "AdPreviewTiktok";

export default AdPreviewTiktok;
